import { encodeCode128 } from './code128';

export type LabelCommandLanguage = 'TSPL' | 'ESCPOS';

export interface LabelItem {
  barcode: string;
  topText?: string;
}

export interface LabelCommandOptions {
  widthMm: number;
  heightMm: number;
  marginXMm: number;
  marginYMm: number;
  gapMm?: number;
  dpi?: number;
  moduleDots?: number;
  offsetXMm?: number;
  offsetYMm?: number;
}

const DEFAULT_GAP_MM = 2;
const DEFAULT_DPI = 203;
const DEFAULT_MODULE_DOTS = 2;
const QUIET_ZONE_MODULES = 10;
const TSPL_FONT3_CHAR_DOTS = 12;

const dotsPerMm = (dpi: number) => dpi / 25.4;

function ascii(text: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < text.length; i += 1) out.push(text.charCodeAt(i) & 0xff);
  return out;
}

function buildTspl(items: readonly LabelItem[], opts: Required<LabelCommandOptions>): number[] {
  const dpmm = dotsPerMm(opts.dpi);
  const labelWidthDots = Math.round(opts.widthMm * dpmm);
  const labelHeightDots = Math.round(opts.heightMm * dpmm);
  const usableDots = Math.max(1, Math.round((opts.widthMm - opts.marginXMm * 2) * dpmm));
  const bytes: number[] = [];
  for (const item of items) {
    const topText = item.topText?.trim();
    const textStripDots = topText ? Math.round(4.5 * dpmm) : 0;
    const barHeightDots = Math.max(1, Math.round((opts.heightMm - opts.marginYMm * 2) * dpmm) - textStripDots);
    const yDots = topText
      ? Math.max(0, Math.round(opts.marginYMm * dpmm) + textStripDots + Math.round(opts.offsetYMm * dpmm))
      : Math.max(0, Math.round((labelHeightDots - barHeightDots) / 2 + opts.offsetYMm * dpmm));
    const dataModules = encodeCode128(item.barcode).totalModules;
    const moduleDots = Math.max(1, Math.floor(usableDots / (dataModules + QUIET_ZONE_MODULES)));
    const barcodeWidthDots = dataModules * moduleDots;
    const xDots = Math.max(0, Math.round((labelWidthDots - barcodeWidthDots) / 2 + opts.offsetXMm * dpmm));
    const textX = Math.max(0, Math.round(labelWidthDots / 2 - ((topText?.length ?? 0) * TSPL_FONT3_CHAR_DOTS) / 2 + opts.offsetXMm * dpmm));
    const textCmd = topText ? `TEXT ${textX},${Math.round(opts.marginYMm * dpmm)},"3",0,1,1,"${topText}"\r\n` : '';
    bytes.push(
      ...ascii(
        `SIZE ${opts.widthMm} mm,${opts.heightMm} mm\r\n` +
          `GAP ${opts.gapMm} mm,0 mm\r\n` +
          'DIRECTION 1\r\n' +
          'CLS\r\n' +
          textCmd +
          `BARCODE ${xDots},${yDots},"128",${barHeightDots},0,0,${moduleDots},${moduleDots},"${item.barcode}"\r\n` +
          'PRINT 1,1\r\n',
      ),
    );
  }
  return bytes;
}

function buildEscPos(items: readonly LabelItem[], opts: Required<LabelCommandOptions>): number[] {
  const dpmm = dotsPerMm(opts.dpi);
  const barHeightDots = Math.min(255, Math.max(1, Math.round((opts.heightMm - opts.marginYMm * 2) * dpmm)));
  const moduleWidth = Math.min(6, Math.max(2, opts.moduleDots));
  const bytes: number[] = [0x1b, 0x40];
  for (const item of items) {
    encodeCode128(item.barcode);
    const data = ascii(`{B${item.barcode}`);
    bytes.push(0x1b, 0x61, 0x01);
    const topText = item.topText?.trim();
    if (topText) bytes.push(0x1d, 0x21, 0x11, ...ascii(topText), 0x0a, 0x1d, 0x21, 0x00);
    bytes.push(0x1d, 0x48, 0x00, 0x1d, 0x68, barHeightDots, 0x1d, 0x77, moduleWidth, 0x1d, 0x6b, 0x49, data.length, ...data, 0x0a, 0x0a, 0x0a);
  }
  return bytes;
}

export function buildLabelCommands(language: LabelCommandLanguage, items: readonly LabelItem[], options: LabelCommandOptions): Uint8Array {
  const opts: Required<LabelCommandOptions> = {
    gapMm: DEFAULT_GAP_MM,
    dpi: DEFAULT_DPI,
    moduleDots: DEFAULT_MODULE_DOTS,
    offsetXMm: 0,
    offsetYMm: 0,
    ...options,
  };
  return Uint8Array.from(language === 'ESCPOS' ? buildEscPos(items, opts) : buildTspl(items, opts));
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i] as number);
  return btoa(binary);
}
